import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import axios from 'axios';

import {
  DollarSign,
  FileText,
  Pencil,
  Plus,
  Printer,
  RefreshCw,
  Trash2,
  TrendingDown,
  TrendingUp,
  X
} from 'lucide-react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import {
  useSettings
} from '../contexts/SettingsContext';

const API_URL =
  process.env.REACT_APP_API_URL ||
  '/api';

const api =
  axios.create({
    baseURL:
      API_URL,
    withCredentials:
      true
  });

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

const CATEGORIAS = [
  'Meta Ads',
  'Google Ads',
  'Assinafy',
  'Servidor / VPS',
  'Software',
  'Fornecedor',
  'Comissão',
  'Salário',
  'Impostos',
  'Administrativo',
  'Outros'
];

function periodoBrasil() {
  const partes =
    new Intl.DateTimeFormat(
      'en-US',
      {
        timeZone:
          'America/Sao_Paulo',
        year:
          'numeric',
        month:
          'numeric',
        day:
          '2-digit'
      }
    )
      .formatToParts(
        new Date()
      );

  const mapa =
    Object.fromEntries(
      partes
        .filter(
          item =>
            item.type !==
            'literal'
        )
        .map(
          item => [
            item.type,
            item.value
          ]
        )
    );

  return {
    year:
      Number(
        mapa.year
      ),
    month:
      Number(
        mapa.month
      ),
    day:
      Number(
        mapa.day
      )
  };
}

function hojeBrasil() {
  const atual =
    periodoBrasil();

  return (
    String(
      atual.year
    ) +
    '-' +
    String(
      atual.month
    ).padStart(2, '0') +
    '-' +
    String(
      atual.day
    ).padStart(2, '0')
  );
}

function moeda(
  valor
) {
  return Number(
    valor || 0
  ).toLocaleString(
    'pt-BR',
    {
      style:
        'currency',
      currency:
        'BRL'
    }
  );
}

function dataBr(
  valor
) {
  if (!valor) {
    return '-';
  }

  const texto =
    String(valor)
      .slice(0, 10);

  const partes =
    texto.split('-');

  if (
    partes.length !== 3
  ) {
    return texto;
  }

  return (
    partes[2] +
    '/' +
    partes[1] +
    '/' +
    partes[0]
  );
}

function dataHoraBr(
  valor
) {
  if (!valor) {
    return '-';
  }

  try {
    return new Date(
      valor
    ).toLocaleString(
      'pt-BR',
      {
        timeZone:
          'America/Sao_Paulo'
      }
    );
  } catch {
    return String(
      valor
    );
  }
}

const Financeiro = () => {
  const {
    darkMode
  } =
    useSettings();

  const atual =
    useMemo(
      () =>
        periodoBrasil(),
      []
    );

  const [
    modo,
    setModo
  ] =
    useState(
      'mensal'
    );

  const [
    year,
    setYear
  ] =
    useState(
      atual.year
    );

  const [
    month,
    setMonth
  ] =
    useState(
      atual.month
    );

  const [
    resumo,
    setResumo
  ] =
    useState({
      entradas: 0,
      despesas: 0,
      resultado: 0,
      transacoes: 0,
      clientes: 0,
      ticket_medio: 0,
      quantidade_despesas: 0
    });

  const [
    despesas,
    setDespesas
  ] =
    useState([]);

  const [
    anual,
    setAnual
  ] =
    useState({
      months: [],
      totals: {
        entradas: 0,
        despesas: 0,
        resultado: 0,
        transacoes: 0
      }
    });

  const [
    loading,
    setLoading
  ] =
    useState(true);

  const [
    busy,
    setBusy
  ] =
    useState(false);

  const [
    erro,
    setErro
  ] =
    useState('');

  const [
    modalDespesa,
    setModalDespesa
  ] =
    useState(false);

  const [
    editando,
    setEditando
  ] =
    useState(null);

  const [
    form,
    setForm
  ] =
    useState({
      expense_date:
        hojeBrasil(),
      amount:
        '',
      category:
        'Outros',
      description:
        '',
      notes:
        ''
    });

  const [
    modalRelatorio,
    setModalRelatorio
  ] =
    useState(false);

  const [
    carregandoRelatorio,
    setCarregandoRelatorio
  ] =
    useState(false);

  const [
    relatorio,
    setRelatorio
  ] =
    useState(null);

  const pageText =
    darkMode
      ? 'text-white'
      : 'text-gray-900';

  const secondary =
    darkMode
      ? 'text-gray-400'
      : 'text-gray-600';

  const card =
    darkMode
      ? 'bg-gray-800 border-gray-700'
      : 'bg-white border-gray-200';

  const input =
    darkMode
      ? 'bg-gray-900 border-gray-700 text-white'
      : 'bg-white border-gray-300 text-gray-900';

  const carregarMensal =
    useCallback(
      async () => {
        setLoading(true);
        setErro('');

        try {
          const [
            resumoResposta,
            despesasResposta
          ] =
            await Promise.all([
              api.get(
                '/financial/summary',
                {
                  params: {
                    year,
                    month
                  }
                }
              ),
              api.get(
                '/financial/expenses',
                {
                  params: {
                    year,
                    month
                  }
                }
              )
            ]);

          setResumo(
            resumoResposta.data ||
            {}
          );

          setDespesas(
            despesasResposta
              .data
              ?.expenses ||
            []
          );
        } catch (error) {
          setErro(
            error.response
              ?.data
              ?.error ||
            'Não foi possível carregar o financeiro.'
          );
        } finally {
          setLoading(false);
        }
      },
      [
        year,
        month
      ]
    );

  const carregarAnual =
    useCallback(
      async () => {
        setLoading(true);
        setErro('');

        try {
          const resposta =
            await api.get(
              '/financial/annual',
              {
                params: {
                  year
                }
              }
            );

          setAnual(
            resposta.data ||
            {
              months: [],
              totals: {}
            }
          );
        } catch (error) {
          setErro(
            error.response
              ?.data
              ?.error ||
            'Não foi possível carregar a visão anual.'
          );
        } finally {
          setLoading(false);
        }
      },
      [
        year
      ]
    );

  useEffect(
    () => {
      if (
        modo === 'mensal'
      ) {
        carregarMensal();
      } else {
        carregarAnual();
      }
    },
    [
      modo,
      carregarMensal,
      carregarAnual
    ]
  );

  function abrirNovaDespesa() {
    setEditando(null);

    setForm({
      expense_date:
        hojeBrasil(),
      amount:
        '',
      category:
        'Outros',
      description:
        '',
      notes:
        ''
    });

    setModalDespesa(
      true
    );
  }

  function abrirEdicao(
    despesa
  ) {
    setEditando(
      despesa
    );

    setForm({
      expense_date:
        String(
          despesa.expense_date ||
          ''
        ).slice(0, 10),
      amount:
        String(
          despesa.amount ??
          ''
        )
          .replace(
            '.',
            ','
          ),
      category:
        despesa.category ||
        'Outros',
      description:
        despesa.description ||
        '',
      notes:
        despesa.notes ||
        ''
    });

    setModalDespesa(
      true
    );
  }

  async function salvarDespesa(
    event
  ) {
    event.preventDefault();

    if (
      !form.expense_date ||
      !String(
        form.amount
      ).trim() ||
      !form.category ||
      !form.description
        .trim()
    ) {
      alert(
        'Preencha data, valor, categoria e descrição.'
      );

      return;
    }

    setBusy(true);

    try {
      if (
        editando?.id
      ) {
        await api.put(
          '/financial/expenses/' +
          editando.id,
          form
        );
      } else {
        await api.post(
          '/financial/expenses',
          form
        );
      }

      setModalDespesa(
        false
      );

      await carregarMensal();
    } catch (error) {
      alert(
        error.response
          ?.data
          ?.error ||
        'Não foi possível salvar a despesa.'
      );
    } finally {
      setBusy(false);
    }
  }

  async function excluirDespesa(
    despesa
  ) {
    if (
      !window.confirm(
        'Excluir a despesa "' +
        despesa.description +
        '" no valor de ' +
        moeda(
          despesa.amount
        ) +
        '?'
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      await api.delete(
        '/financial/expenses/' +
        despesa.id
      );

      await carregarMensal();
    } catch (error) {
      alert(
        error.response
          ?.data
          ?.error ||
        'Não foi possível excluir a despesa.'
      );
    } finally {
      setBusy(false);
    }
  }

  async function gerarRelatorio() {
    setCarregandoRelatorio(
      true
    );

    try {
      const resposta =
        await api.get(
          '/financial/report',
          {
            params: {
              year,
              month
            }
          }
        );

      setRelatorio(
        resposta.data
      );

      setModalRelatorio(
        true
      );
    } catch (error) {
      alert(
        error.response
          ?.data
          ?.error ||
        'Não foi possível gerar o relatório.'
      );
    } finally {
      setCarregandoRelatorio(
        false
      );
    }
  }

  function imprimirRelatorio() {
    window.print();
  }

  const anos =
    useMemo(
      () => {
        const lista = [];

        for (
          let ano =
            atual.year - 4;
          ano <=
            atual.year + 2;
          ano += 1
        ) {
          lista.push(
            ano
          );
        }

        return lista;
      },
      [
        atual.year
      ]
    );

  const dadosGrafico =
    useMemo(
      () =>
        (
          anual.months ||
          []
        ).map(
          item => ({
            mes:
              MESES[
                Number(
                  item.month
                ) - 1
              ]?.slice(
                0,
                3
              ) ||
              item.month,
            Entradas:
              Number(
                item.entradas ||
                0
              ),
            Despesas:
              Number(
                item.despesas ||
                0
              ),
            Resultado:
              Number(
                item.resultado ||
                0
              )
          })
        ),
      [
        anual
      ]
    );

  const cardsMensais = [
    {
      titulo:
        'Entradas',
      valor:
        moeda(
          resumo.entradas
        ),
      detalhe:
        `${resumo.transacoes || 0} pagamento(s) confirmado(s)`,
      icon:
        TrendingUp,
      iconClass:
        'bg-green-500'
    },
    {
      titulo:
        'Despesas',
      valor:
        moeda(
          resumo.despesas
        ),
      detalhe:
        `${resumo.quantidade_despesas || 0} despesa(s)`,
      icon:
        TrendingDown,
      iconClass:
        'bg-red-500'
    },
    {
      titulo:
        'Resultado líquido',
      valor:
        moeda(
          resumo.resultado
        ),
      detalhe:
        'Entradas menos despesas',
      icon:
        DollarSign,
      iconClass:
        Number(
          resumo.resultado ||
          0
        ) >= 0
          ? 'bg-blue-500'
          : 'bg-red-600'
    },
    {
      titulo:
        'Ticket médio',
      valor:
        moeda(
          resumo.ticket_medio
        ),
      detalhe:
        `${resumo.clientes || 0} cliente(s) com pagamento`,
      icon:
        FileText,
      iconClass:
        'bg-purple-500'
    }
  ];

  return (
    <div
      className="space-y-6"
    >
      <div
        className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4"
      >
        <div>
          <h1
            className={
              `text-3xl font-bold ${pageText}`
            }
          >
            Financeiro
          </h1>

          <p
            className={
              secondary
            }
          >
            Entradas automáticas das vendas confirmadas e controle das despesas.
          </p>
        </div>

        <div
          className="flex flex-wrap gap-2"
        >
          <button
            type="button"
            onClick={() =>
              setModo(
                'mensal'
              )
            }
            className={
              modo ===
              'mensal'
                ? 'px-4 py-2 rounded-lg bg-blue-600 text-white font-medium'
                : `px-4 py-2 rounded-lg border ${card} ${pageText}`
            }
          >
            Mensal
          </button>

          <button
            type="button"
            onClick={() =>
              setModo(
                'anual'
              )
            }
            className={
              modo ===
              'anual'
                ? 'px-4 py-2 rounded-lg bg-blue-600 text-white font-medium'
                : `px-4 py-2 rounded-lg border ${card} ${pageText}`
            }
          >
            Anual
          </button>

          <button
            type="button"
            onClick={
              modo ===
              'mensal'
                ? carregarMensal
                : carregarAnual
            }
            className={
              `px-4 py-2 rounded-lg border flex items-center gap-2 ${card} ${pageText}`
            }
          >
            <RefreshCw
              size={17}
            />
            Atualizar
          </button>

          <button
            type="button"
            onClick={
              abrirNovaDespesa
            }
            className="px-4 py-2 rounded-lg bg-blue-600 text-white flex items-center gap-2 font-medium"
          >
            <Plus
              size={18}
            />
            Nova despesa
          </button>
        </div>
      </div>

      <div
        className={
          `border rounded-xl p-4 ${card}`
        }
      >
        <div
          className="flex flex-wrap items-end gap-4"
        >
          <div>
            <label
              className={
                `block text-sm font-medium mb-1 ${secondary}`
              }
            >
              Ano
            </label>

            <select
              value={
                year
              }
              onChange={
                event =>
                  setYear(
                    Number(
                      event.target.value
                    )
                  )
              }
              className={
                `border rounded-lg px-3 py-2 ${input}`
              }
            >
              {anos.map(
                ano => (
                  <option
                    key={
                      ano
                    }
                    value={
                      ano
                    }
                  >
                    {ano}
                  </option>
                )
              )}
            </select>
          </div>

          {modo ===
            'mensal' && (
            <div>
              <label
                className={
                  `block text-sm font-medium mb-1 ${secondary}`
                }
              >
                Mês
              </label>

              <select
                value={
                  month
                }
                onChange={
                  event =>
                    setMonth(
                      Number(
                        event.target.value
                      )
                    )
                }
                className={
                  `border rounded-lg px-3 py-2 ${input}`
                }
              >
                {MESES.map(
                  (
                    nome,
                    index
                  ) => (
                    <option
                      key={
                        nome
                      }
                      value={
                        index + 1
                      }
                    >
                      {nome}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

          {modo ===
            'mensal' && (
            <button
              type="button"
              onClick={
                gerarRelatorio
              }
              disabled={
                carregandoRelatorio
              }
              className="px-4 py-2 rounded-lg bg-gray-700 text-white flex items-center gap-2 font-medium disabled:opacity-50"
            >
              <FileText
                size={18}
              />

              {carregandoRelatorio
                ? 'Gerando...'
                : 'Gerar relatório'}
            </button>
          )}
        </div>
      </div>

      {erro && (
        <div
          className="rounded-lg border border-red-300 bg-red-50 text-red-700 px-4 py-3"
        >
          {erro}
        </div>
      )}

      {loading ? (
        <div
          className="flex justify-center py-16"
        >
          <div
            className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"
          />
        </div>
      ) : modo ===
        'mensal' ? (
        <>
          <div
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6"
          >
            {cardsMensais.map(
              item => {
                const Icon =
                  item.icon;

                return (
                  <div
                    key={
                      item.titulo
                    }
                    className={
                      `border rounded-xl shadow-sm p-6 ${card}`
                    }
                  >
                    <div
                      className="flex items-center justify-between"
                    >
                      <div>
                        <p
                          className={
                            `text-sm ${secondary}`
                          }
                        >
                          {item.titulo}
                        </p>

                        <p
                          className={
                            `text-2xl font-bold mt-1 ${pageText}`
                          }
                        >
                          {item.valor}
                        </p>
                      </div>

                      <div
                        className={
                          `${item.iconClass} p-3 rounded-lg`
                        }
                      >
                        <Icon
                          className="text-white"
                          size={24}
                        />
                      </div>
                    </div>

                    <p
                      className={
                        `mt-4 text-sm ${secondary}`
                      }
                    >
                      {item.detalhe}
                    </p>
                  </div>
                );
              }
            )}
          </div>

          <div
            className={
              `border rounded-xl shadow-sm ${card}`
            }
          >
            <div
              className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
            >
              <div>
                <h2
                  className={
                    `text-lg font-semibold ${pageText}`
                  }
                >
                  Despesas de {MESES[month - 1]} de {year}
                </h2>

                <p
                  className={
                    `text-sm ${secondary}`
                  }
                >
                  No painel aparecem os valores principais. A descrição completa também entra no relatório.
                </p>
              </div>
            </div>

            <div
              className="overflow-x-auto"
            >
              <table
                className="w-full"
              >
                <thead>
                  <tr
                    className={
                      darkMode
                        ? 'text-left text-sm text-gray-400 border-b border-gray-700'
                        : 'text-left text-sm text-gray-500 border-b border-gray-200'
                    }
                  >
                    <th
                      className="px-5 py-3"
                    >
                      Data
                    </th>
                    <th
                      className="px-5 py-3"
                    >
                      Categoria
                    </th>
                    <th
                      className="px-5 py-3"
                    >
                      Descrição
                    </th>
                    <th
                      className="px-5 py-3 text-right"
                    >
                      Valor
                    </th>
                    <th
                      className="px-5 py-3 text-right"
                    >
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {despesas.length ===
                  0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className={
                          `px-5 py-12 text-center ${secondary}`
                        }
                      >
                        Nenhuma despesa cadastrada neste mês.
                      </td>
                    </tr>
                  ) : (
                    despesas.map(
                      despesa => (
                        <tr
                          key={
                            despesa.id
                          }
                          className={
                            darkMode
                              ? 'border-b border-gray-700'
                              : 'border-b border-gray-100'
                          }
                        >
                          <td
                            className={
                              `px-5 py-4 ${pageText}`
                            }
                          >
                            {dataBr(
                              despesa.expense_date
                            )}
                          </td>

                          <td
                            className="px-5 py-4"
                          >
                            <span
                              className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                            >
                              {despesa.category}
                            </span>
                          </td>

                          <td
                            className="px-5 py-4"
                          >
                            <div
                              className={
                                pageText
                              }
                            >
                              {despesa.description}
                            </div>

                            {despesa.notes && (
                              <div
                                className={
                                  `text-xs mt-1 ${secondary}`
                                }
                              >
                                {despesa.notes}
                              </div>
                            )}
                          </td>

                          <td
                            className="px-5 py-4 text-right font-semibold text-red-500"
                          >
                            {moeda(
                              despesa.amount
                            )}
                          </td>

                          <td
                            className="px-5 py-4"
                          >
                            <div
                              className="flex justify-end gap-2"
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  abrirEdicao(
                                    despesa
                                  )
                                }
                                className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                                title="Editar"
                              >
                                <Pencil
                                  size={16}
                                />
                              </button>

                              <button
                                type="button"
                                disabled={
                                  busy
                                }
                                onClick={() =>
                                  excluirDespesa(
                                    despesa
                                  )
                                }
                                className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                                title="Excluir"
                              >
                                <Trash2
                                  size={16}
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <div
              className={
                `border rounded-xl p-6 ${card}`
              }
            >
              <p
                className={
                  `text-sm ${secondary}`
                }
              >
                Entradas no ano
              </p>

              <p
                className={
                  `text-2xl font-bold mt-1 ${pageText}`
                }
              >
                {moeda(
                  anual.totals
                    ?.entradas
                )}
              </p>
            </div>

            <div
              className={
                `border rounded-xl p-6 ${card}`
              }
            >
              <p
                className={
                  `text-sm ${secondary}`
                }
              >
                Despesas no ano
              </p>

              <p
                className="text-2xl font-bold mt-1 text-red-500"
              >
                {moeda(
                  anual.totals
                    ?.despesas
                )}
              </p>
            </div>

            <div
              className={
                `border rounded-xl p-6 ${card}`
              }
            >
              <p
                className={
                  `text-sm ${secondary}`
                }
              >
                Resultado líquido no ano
              </p>

              <p
                className={
                  `text-2xl font-bold mt-1 ${
                    Number(
                      anual.totals
                        ?.resultado ||
                      0
                    ) >= 0
                      ? 'text-green-500'
                      : 'text-red-500'
                  }`
                }
              >
                {moeda(
                  anual.totals
                    ?.resultado
                )}
              </p>
            </div>
          </div>

          <div
            className={
              `border rounded-xl p-6 ${card}`
            }
          >
            <h2
              className={
                `text-lg font-semibold mb-5 ${pageText}`
              }
            >
              Evolução financeira de {year}
            </h2>

            <ResponsiveContainer
              width="100%"
              height={330}
            >
              <BarChart
                data={
                  dadosGrafico
                }
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={
                    darkMode
                      ? '#374151'
                      : '#e5e7eb'
                  }
                />

                <XAxis
                  dataKey="mes"
                  stroke={
                    darkMode
                      ? '#9ca3af'
                      : '#6b7280'
                  }
                />

                <YAxis
                  stroke={
                    darkMode
                      ? '#9ca3af'
                      : '#6b7280'
                  }
                />

                <Tooltip
                  formatter={
                    valor =>
                      moeda(
                        valor
                      )
                  }
                  contentStyle={{
                    backgroundColor:
                      darkMode
                        ? '#1f2937'
                        : '#ffffff',
                    color:
                      darkMode
                        ? '#ffffff'
                        : '#000000'
                  }}
                />

                <Legend />

                <Bar
                  dataKey="Entradas"
                  fill="#22c55e"
                />

                <Bar
                  dataKey="Despesas"
                  fill="#ef4444"
                />

                <Bar
                  dataKey="Resultado"
                  fill="#3b82f6"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div
            className={
              `border rounded-xl overflow-hidden ${card}`
            }
          >
            <div
              className="overflow-x-auto"
            >
              <table
                className="w-full"
              >
                <thead>
                  <tr
                    className={
                      darkMode
                        ? 'text-left text-sm text-gray-400 border-b border-gray-700'
                        : 'text-left text-sm text-gray-500 border-b border-gray-200'
                    }
                  >
                    <th
                      className="px-5 py-3"
                    >
                      Mês
                    </th>
                    <th
                      className="px-5 py-3 text-right"
                    >
                      Entradas
                    </th>
                    <th
                      className="px-5 py-3 text-right"
                    >
                      Despesas
                    </th>
                    <th
                      className="px-5 py-3 text-right"
                    >
                      Resultado
                    </th>
                    <th
                      className="px-5 py-3 text-right"
                    >
                      Transações
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {(anual.months ||
                    []).map(
                    item => (
                      <tr
                        key={
                          item.month
                        }
                        className={
                          darkMode
                            ? 'border-b border-gray-700'
                            : 'border-b border-gray-100'
                        }
                      >
                        <td
                          className={
                            `px-5 py-4 font-medium ${pageText}`
                          }
                        >
                          {MESES[
                            Number(
                              item.month
                            ) - 1
                          ]}
                        </td>

                        <td
                          className="px-5 py-4 text-right text-green-500"
                        >
                          {moeda(
                            item.entradas
                          )}
                        </td>

                        <td
                          className="px-5 py-4 text-right text-red-500"
                        >
                          {moeda(
                            item.despesas
                          )}
                        </td>

                        <td
                          className={
                            `px-5 py-4 text-right font-semibold ${
                              Number(
                                item.resultado ||
                                0
                              ) >= 0
                                ? 'text-blue-500'
                                : 'text-red-500'
                            }`
                          }
                        >
                          {moeda(
                            item.resultado
                          )}
                        </td>

                        <td
                          className={
                            `px-5 py-4 text-right ${pageText}`
                          }
                        >
                          {item.transacoes ||
                            0}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {modalDespesa && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        >
          <div
            className={
              `w-full max-w-xl rounded-xl shadow-xl border ${card}`
            }
          >
            <div
              className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700"
            >
              <h2
                className={
                  `text-xl font-bold ${pageText}`
                }
              >
                {editando
                  ? 'Editar despesa'
                  : 'Nova despesa'}
              </h2>

              <button
                type="button"
                onClick={() =>
                  setModalDespesa(
                    false
                  )
                }
                className={
                  secondary
                }
              >
                <X
                  size={22}
                />
              </button>
            </div>

            <form
              onSubmit={
                salvarDespesa
              }
              className="p-5 space-y-4"
            >
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div>
                  <label
                    className={
                      `block text-sm font-medium mb-1 ${secondary}`
                    }
                  >
                    Data
                  </label>

                  <input
                    type="date"
                    required
                    value={
                      form.expense_date
                    }
                    onChange={
                      event =>
                        setForm({
                          ...form,
                          expense_date:
                            event.target.value
                        })
                    }
                    className={
                      `w-full border rounded-lg px-3 py-2 ${input}`
                    }
                  />
                </div>

                <div>
                  <label
                    className={
                      `block text-sm font-medium mb-1 ${secondary}`
                    }
                  >
                    Valor
                  </label>

                  <input
                    type="text"
                    inputMode="decimal"
                    required
                    placeholder="Ex.: 1.500,50"
                    value={
                      form.amount
                    }
                    onChange={
                      event =>
                        setForm({
                          ...form,
                          amount:
                            event.target.value
                        })
                    }
                    className={
                      `w-full border rounded-lg px-3 py-2 ${input}`
                    }
                  />
                </div>
              </div>

              <div>
                <label
                  className={
                    `block text-sm font-medium mb-1 ${secondary}`
                  }
                >
                  Categoria
                </label>

                <select
                  required
                  value={
                    form.category
                  }
                  onChange={
                    event =>
                      setForm({
                        ...form,
                        category:
                          event.target.value
                      })
                  }
                  className={
                    `w-full border rounded-lg px-3 py-2 ${input}`
                  }
                >
                  {CATEGORIAS.map(
                    categoria => (
                      <option
                        key={
                          categoria
                        }
                        value={
                          categoria
                        }
                      >
                        {categoria}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label
                  className={
                    `block text-sm font-medium mb-1 ${secondary}`
                  }
                >
                  Descrição
                </label>

                <input
                  type="text"
                  required
                  maxLength={255}
                  placeholder="Ex.: Campanha Rating São Paulo"
                  value={
                    form.description
                  }
                  onChange={
                    event =>
                      setForm({
                        ...form,
                        description:
                          event.target.value
                      })
                  }
                  className={
                    `w-full border rounded-lg px-3 py-2 ${input}`
                  }
                />
              </div>

              <div>
                <label
                  className={
                    `block text-sm font-medium mb-1 ${secondary}`
                  }
                >
                  Observação
                  <span
                    className="font-normal"
                  >
                    {' '}
                    (opcional)
                  </span>
                </label>

                <textarea
                  rows={3}
                  maxLength={4000}
                  value={
                    form.notes
                  }
                  onChange={
                    event =>
                      setForm({
                        ...form,
                        notes:
                          event.target.value
                      })
                  }
                  className={
                    `w-full border rounded-lg px-3 py-2 ${input}`
                  }
                />
              </div>

              <div
                className="flex justify-end gap-3 pt-2"
              >
                <button
                  type="button"
                  onClick={() =>
                    setModalDespesa(
                      false
                    )
                  }
                  className={
                    `px-4 py-2 rounded-lg border ${card} ${pageText}`
                  }
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={
                    busy
                  }
                  className="px-5 py-2 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-50"
                >
                  {busy
                    ? 'Salvando...'
                    : 'Salvar despesa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalRelatorio &&
        relatorio && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
        >
          <div
            className={
              `w-full max-w-5xl rounded-xl shadow-xl border my-6 ${card}`
            }
          >
            <div
              className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700"
            >
              <div>
                <h2
                  className={
                    `text-xl font-bold ${pageText}`
                  }
                >
                  Relatório Financeiro
                </h2>

                <p
                  className={
                    secondary
                  }
                >
                  {MESES[
                    Number(
                      relatorio.month
                    ) - 1
                  ]}{' '}
                  de{' '}
                  {relatorio.year}
                </p>
              </div>

              <div
                className="flex items-center gap-2"
              >
                <button
                  type="button"
                  onClick={
                    imprimirRelatorio
                  }
                  className="px-3 py-2 rounded-lg bg-gray-700 text-white flex items-center gap-2"
                >
                  <Printer
                    size={17}
                  />
                  Imprimir
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setModalRelatorio(
                      false
                    )
                  }
                  className={
                    secondary
                  }
                >
                  <X
                    size={22}
                  />
                </button>
              </div>
            </div>

            <div
              className="p-5 space-y-6"
            >
              <div
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <div
                  className={
                    `border rounded-lg p-4 ${card}`
                  }
                >
                  <p
                    className={
                      `text-sm ${secondary}`
                    }
                  >
                    Entradas
                  </p>

                  <p
                    className="text-xl font-bold text-green-500"
                  >
                    {moeda(
                      relatorio
                        .summary
                        ?.entradas
                    )}
                  </p>
                </div>

                <div
                  className={
                    `border rounded-lg p-4 ${card}`
                  }
                >
                  <p
                    className={
                      `text-sm ${secondary}`
                    }
                  >
                    Despesas
                  </p>

                  <p
                    className="text-xl font-bold text-red-500"
                  >
                    {moeda(
                      relatorio
                        .summary
                        ?.despesas
                    )}
                  </p>
                </div>

                <div
                  className={
                    `border rounded-lg p-4 ${card}`
                  }
                >
                  <p
                    className={
                      `text-sm ${secondary}`
                    }
                  >
                    Resultado
                  </p>

                  <p
                    className={
                      `text-xl font-bold ${
                        Number(
                          relatorio
                            .summary
                            ?.resultado ||
                          0
                        ) >= 0
                          ? 'text-blue-500'
                          : 'text-red-500'
                      }`
                    }
                  >
                    {moeda(
                      relatorio
                        .summary
                        ?.resultado
                    )}
                  </p>
                </div>
              </div>

              <div>
                <h3
                  className={
                    `text-lg font-semibold mb-3 ${pageText}`
                  }
                >
                  Entradas
                </h3>

                <div
                  className="overflow-x-auto"
                >
                  <table
                    className="w-full"
                  >
                    <thead>
                      <tr
                        className={
                          darkMode
                            ? 'text-left text-sm text-gray-400 border-b border-gray-700'
                            : 'text-left text-sm text-gray-500 border-b border-gray-200'
                        }
                      >
                        <th
                          className="py-2 pr-4"
                        >
                          Data
                        </th>
                        <th
                          className="py-2 pr-4"
                        >
                          Cliente
                        </th>
                        <th
                          className="py-2 pr-4"
                        >
                          Forma
                        </th>
                        <th
                          className="py-2 text-right"
                        >
                          Valor
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {(relatorio.entries ||
                        []).map(
                        entrada => (
                          <tr
                            key={
                              entrada.id
                            }
                            className={
                              darkMode
                                ? 'border-b border-gray-700'
                                : 'border-b border-gray-100'
                            }
                          >
                            <td
                              className={
                                `py-3 pr-4 ${pageText}`
                              }
                            >
                              {dataHoraBr(
                                entrada.paid_at
                              )}
                            </td>

                            <td
                              className={
                                `py-3 pr-4 ${pageText}`
                              }
                            >
                              {entrada.cliente}
                            </td>

                            <td
                              className={
                                `py-3 pr-4 ${secondary}`
                              }
                            >
                              {entrada.payment_method ||
                                '-'}
                            </td>

                            <td
                              className="py-3 text-right font-medium text-green-500"
                            >
                              {moeda(
                                entrada.amount
                              )}
                            </td>
                          </tr>
                        )
                      )}

                      {(relatorio.entries ||
                        []).length ===
                        0 && (
                        <tr>
                          <td
                            colSpan="4"
                            className={
                              `py-6 text-center ${secondary}`
                            }
                          >
                            Nenhuma entrada neste período.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3
                  className={
                    `text-lg font-semibold mb-3 ${pageText}`
                  }
                >
                  Despesas
                </h3>

                <div
                  className="overflow-x-auto"
                >
                  <table
                    className="w-full"
                  >
                    <thead>
                      <tr
                        className={
                          darkMode
                            ? 'text-left text-sm text-gray-400 border-b border-gray-700'
                            : 'text-left text-sm text-gray-500 border-b border-gray-200'
                        }
                      >
                        <th
                          className="py-2 pr-4"
                        >
                          Data
                        </th>
                        <th
                          className="py-2 pr-4"
                        >
                          Categoria
                        </th>
                        <th
                          className="py-2 pr-4"
                        >
                          Descrição
                        </th>
                        <th
                          className="py-2 pr-4"
                        >
                          Observação
                        </th>
                        <th
                          className="py-2 text-right"
                        >
                          Valor
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {(relatorio.expenses ||
                        []).map(
                        despesa => (
                          <tr
                            key={
                              despesa.id
                            }
                            className={
                              darkMode
                                ? 'border-b border-gray-700'
                                : 'border-b border-gray-100'
                            }
                          >
                            <td
                              className={
                                `py-3 pr-4 ${pageText}`
                              }
                            >
                              {dataBr(
                                despesa.expense_date
                              )}
                            </td>

                            <td
                              className={
                                `py-3 pr-4 ${pageText}`
                              }
                            >
                              {despesa.category}
                            </td>

                            <td
                              className={
                                `py-3 pr-4 ${pageText}`
                              }
                            >
                              {despesa.description}
                            </td>

                            <td
                              className={
                                `py-3 pr-4 ${secondary}`
                              }
                            >
                              {despesa.notes ||
                                '-'}
                            </td>

                            <td
                              className="py-3 text-right font-medium text-red-500"
                            >
                              {moeda(
                                despesa.amount
                              )}
                            </td>
                          </tr>
                        )
                      )}

                      {(relatorio.expenses ||
                        []).length ===
                        0 && (
                        <tr>
                          <td
                            colSpan="5"
                            className={
                              `py-6 text-center ${secondary}`
                            }
                          >
                            Nenhuma despesa neste período.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Financeiro;
